<template>
    <div class="ai-plugins">
        <div class="plugins-header">
            <div class="plugins-title">{{ $t("chatbotv2.aiPluginsTitle") }}</div>
            <div class="plugins-subtitle">
                {{ $t("chatbotv2.aiPluginsSubtitle") }}
            </div>
        </div>

        <!-- Response Healing -->
        <div class="plugin-card">
            <div class="plugin-card-header">
                <div class="plugin-info">
                    <div class="plugin-name">
                        <span class="plugin-icon">🩹</span>
                        Response Healing
                        <a-tag color="purple" class="plugin-tag">{{ $t("chatbotv2.jsonRepair") }}</a-tag>
                    </div>
                    <div class="plugin-desc">
                        {{ $t("chatbotv2.responseHealingDesc") }}
                    </div>
                </div>
                <a-switch :checked="plugins.responseHealing.enabled" @change="(v) => togglePlugin('responseHealing', v)" />
            </div>

            <div v-if="plugins.responseHealing.enabled" class="plugin-detail">
                <a-alert type="info" show-icon>
                    <template slot="message">Response Healing Nasıl Çalışır?</template>
                    <template slot="description">
                        <p>Bu plugin, <code>response_format</code> ile <code>json_schema</code> veya <code>json_object</code> kullandığınızda devreye girer ve aşağıdaki sorunları düzeltir:</p>
                        <ul>
                            <li><b>JSON Sözdizimi Hataları</b> – Eksik kapanış parantezleri, çift tırnak sorunları vb.</li>
                            <li><b>Markdown Sarmalama</b> – <code>```json ... ```</code> bloklarından JSON'ı çıkarır.</li>
                            <li><b>Metin + JSON Karışımı</b> – Yanıtın başındaki metin açıklamalarını siler, yalnızca JSON'ı alır.</li>
                            <li><b>Sondaki Virgüller</b> – <code>{"key": "val",}</code> gibi geçersiz virgülleri kaldırır.</li>
                            <li><b>Tırnaksız Anahtarlar</b> – <code>{key: "val"}</code> → <code>{"key": "val"}</code> şekline dönüştürür.</li>
                        </ul>
                        <a-alert type="warning" style="margin-top:8px">
                            <template slot="message">Yalnızca <b>streaming olmayan</b> isteklerde çalışır. Kesilmiş (max_tokens aşımı) yanıtlar onarılamayabilir.</template>
                        </a-alert>
                    </template>
                </a-alert>
            </div>
        </div>

        <!-- PDF Inputs -->
        <div class="plugin-card">
            <div class="plugin-card-header">
                <div class="plugin-info">
                    <div class="plugin-name">
                        <span class="plugin-icon">📄</span>
                        PDF Inputs
                        <a-tag color="orange" class="plugin-tag">Dosya İşleme</a-tag>
                    </div>
                    <div class="plugin-desc">
                        Yüklenen PDF dosyalarını ayrıştırarak içeriklerini LLM'e metin olarak iletir. Dosya girişini desteklemeyen modeller dahil tüm modellerde PDF analizi yapmanızı sağlar.
                    </div>
                </div>
                <a-switch :checked="plugins.pdfInput.enabled" @change="(v) => togglePlugin('pdfInput', v)" />
            </div>

            <div v-if="plugins.pdfInput.enabled" class="plugin-detail">
                <div class="detail-section">
                    <div class="detail-label">PDF İşleme Motoru <span class="required">*</span></div>
                    <div class="detail-desc">Hangi motorun kullanılacağını seçin. Fiyatlandırma motorlara göre değişir ve Supsis %10 işlem ücreti ekler.</div>
                    <div class="engine-cards">
                        <div
                            v-for="eng in pdfEngines"
                            :key="eng.id"
                            class="engine-card"
                            :class="{ selected: plugins.pdfInput.engine === eng.id }"
                            @click="setPdfEngine(eng.id)"
                        >
                            <div class="engine-card-header">
                                <span class="engine-name">{{ eng.name }}</span>
                                <a-tag v-if="eng.recommended" color="green" style="font-size:11px">Önerilen</a-tag>
                                <a-tag :color="eng.free ? 'green' : 'orange'" style="font-size:11px">{{ eng.priceLabel }}</a-tag>
                            </div>
                            <div class="engine-desc">{{ eng.description }}</div>
                        </div>
                    </div>
                </div>

                <a-alert type="info" show-icon style="margin-top:12px">
                    <template slot="message">PDF Inputs Nasıl Çalışır?</template>
                    <template slot="description">
                        <p>PDF dosyaları URL veya base64 ile gönderilebilir. OpenRouter tüm modellerde PDF desteği sağlar:</p>
                        <ul>
                            <li><b>Doğal Destek</b>: Model PDF'yi doğrudan işliyorsa (native engine), dosya direkt iletilir.</li>
                            <li><b>Metin Dönüşümü</b>: Model dosya desteklemiyorsa, OpenRouter PDF'yi ayrıştırarak içeriği metin olarak gönderir.</li>
                        </ul>
                        <div style="margin-top:8px">
                            <b>Fiyatlandırma (Supsis %10 ücret ekler):</b>
                            <ul>
                                <li><code>mistral-ocr</code> – ~$2.20 / 1.000 sayfa (Supsis %10 dahil)</li>
                                <li><code>pdf-text</code> – Ücretsiz</li>
                                <li><code>native</code> – Input token ücreti kadar (model provider'a göre değişir)</li>
                            </ul>
                        </div>
                    </template>
                </a-alert>
            </div>
        </div>

        <!-- Web Search -->
        <div class="plugin-card">
            <div class="plugin-card-header">
                <div class="plugin-info">
                    <div class="plugin-name">
                        <span class="plugin-icon">🌐</span>
                        Web Search
                        <a-tag color="blue" class="plugin-tag">Gerçek Zamanlı Arama</a-tag>
                    </div>
                    <div class="plugin-desc">
                        LLM yanıtlarını gerçek zamanlı web arama sonuçlarıyla zenginleştirir. Herhangi bir modelde güncel bilgiye erişim sağlar.
                    </div>
                </div>
                <a-switch :checked="plugins.webSearch.enabled" @change="(v) => togglePlugin('webSearch', v)" />
            </div>

            <div v-if="plugins.webSearch.enabled" class="plugin-detail">
                <div class="detail-row">
                    <div class="detail-section" style="flex:1">
                        <div class="detail-label">Arama Motoru</div>
                        <div class="detail-desc">Hangi arama motorunun kullanılacağını seçin.</div>
                        <a-select v-model="plugins.webSearch.engine" style="width:100%" @change="savePluginField('webSearch', 'engine', $event)">
                            <a-select-option value="">Otomatik (Native varsa Native, yoksa Exa)</a-select-option>
                            <a-select-option value="native">Native – LLM sağlayıcısının yerleşik web araması</a-select-option>
                            <a-select-option value="exa">Exa – Exa.ai web arama API'si</a-select-option>
                        </a-select>
                    </div>
                    <div class="detail-section" style="flex:1">
                        <div class="detail-label">Maks. Sonuç Sayısı</div>
                        <div class="detail-desc">Döndürülecek web arama sonucu sayısı (varsayılan: 5).</div>
                        <a-input-number
                            v-model="plugins.webSearch.maxResults"
                            :min="1"
                            :max="10"
                            style="width:100%"
                            @change="savePluginField('webSearch', 'maxResults', $event)"
                        />
                    </div>
                </div>

                <div class="detail-section" style="margin-top:12px">
                    <div class="detail-label">Arama Bağlam Boyutu (Native modeller)</div>
                    <div class="detail-desc">Native modellerde ne kadar arama bağlamı alınacağını belirler. Daha fazla bağlam daha yüksek maliyet demektir.</div>
                    <div class="context-cards">
                        <div
                            v-for="ctx in contextSizes"
                            :key="ctx.id"
                            class="context-card"
                            :class="{ selected: plugins.webSearch.searchContextSize === ctx.id }"
                            @click="setContextSize(ctx.id)"
                        >
                            <div class="context-name">{{ ctx.name }}</div>
                            <div class="context-desc">{{ ctx.description }}</div>
                        </div>
                    </div>
                </div>

                <a-alert type="info" show-icon style="margin-top:12px">
                    <template slot="message">Web Search Nasıl Çalışır?</template>
                    <template slot="description">
                        <p>Web Search plugin'i, seçilen modele göre farklı motorlar kullanır:</p>
                        <ul>
                            <li><b>Native</b> – OpenAI, Anthropic, Perplexity, xAI modellerinde yerleşik arama kullanılır. xAI'de hem Web hem X araması etkinleşir.</li>
                            <li><b>Exa</b> – Diğer tüm modellerde Exa.ai'nin arama API'si kullanılır. Anahtar kelime + embedding tabanlı "auto" modu ile en alakalı sonuçları bulur.</li>
                            <li><b>Otomatik</b> – Native arama varsa kullanılır, yoksa Exa'ya geçilir.</li>
                        </ul>
                        <div style="margin-top:8px">
                            <b>Fiyatlandırma (Supsis %10 ücret ekler):</b>
                            <ul>
                                <li><b>Exa araması</b> – ~$4.40 / 1.000 sonuç (Supsis %10 dahil). Varsayılan 5 sonuçta ~$0,022 / istek.</li>
                                <li><b>Native arama</b> – Sağlayıcı fiyatına eklenir:
                                    <a href="https://platform.openai.com/docs/pricing#built-in-tools" target="_blank">OpenAI</a>,
                                    <a href="https://docs.claude.com/en/docs/agents-and-tools/tool-use/web-search-tool#usage-and-pricing" target="_blank">Anthropic</a>,
                                    <a href="https://docs.perplexity.ai/getting-started/pricing" target="_blank">Perplexity</a>,
                                    <a href="https://docs.x.ai/docs/models#tool-invocation-costs" target="_blank">xAI</a>
                                </li>
                            </ul>
                        </div>
                    </template>
                </a-alert>
            </div>
        </div>

        <!-- Message Transform -->
        <div class="plugin-card">
            <div class="plugin-card-header">
                <div class="plugin-info">
                    <div class="plugin-name">
                        <span class="plugin-icon">✂️</span>
                        Message Transform (Middle-Out)
                        <a-tag color="geekblue" class="plugin-tag">Context Yönetimi</a-tag>
                    </div>
                    <div class="plugin-desc">
                        Context penceresi aşıldığında orta kısımdaki mesajları kırparak isteğin başarılı olmasını sağlar. LLM'lerin orta konumlara daha az dikkat ettiği bulgusu üzerine kurulmuştur.
                    </div>
                </div>
                <a-switch :checked="plugins.messageTransform.enabled" @change="(v) => togglePlugin('messageTransform', v)" />
            </div>

            <div v-if="plugins.messageTransform.enabled" class="plugin-detail">
                <a-alert type="info" show-icon>
                    <template slot="message">Message Transform Nasıl Çalışır?</template>
                    <template slot="description">
                        <p>Mesaj geçmişi modelin maksimum context penceresini aştığında, bu dönüşüm otomatik olarak devreye girer:</p>
                        <ul>
                            <li>Konuşmanın <b>ortasındaki</b> mesajlar kaldırılır veya kısaltılır.</li>
                            <li><b>Başlangıç ve son</b> mesajlar korunur; bu sayede bağlam bütünlüğü sağlanır.</li>
                            <li>Anthropic Claude gibi modellerde uygulanan <b>maksimum mesaj sayısı</b> sınırı da aşıldığında devreye girer: baştan ve sondan eşit sayıda mesaj tutulur.</li>
                        </ul>
                        <div style="margin-top:8px">
                            <b>Ne zaman kullanmalısınız?</b>
                            <ul>
                                <li>✅ Uzun konuşmalar ve geniş knowledge base içerikleri olan chatbotlarda kesinlikle önerilir.</li>
                                <li>✅ Context taşması nedeniyle hata alıyorsanız açın.</li>
                                <li>⚠️ Eksiksiz hatırlama kritikse (hukuki, tıbbi gibi alanlarda) dikkatli kullanın; orta mesajlar silinebilir.</li>
                            </ul>
                        </div>
                        <a-alert type="warning" style="margin-top:8px">
                            <template slot="message">Not: 8.192 token veya daha küçük context'e sahip OpenRouter modellerinde bu dönüşüm zaten <b>varsayılan olarak aktiftir</b>. Büyük context modelleri için manuel etkinleştirin.</template>
                        </a-alert>
                    </template>
                </a-alert>
            </div>
        </div>

        <!-- RAG Query Rewriter -->
        <div class="plugin-card">
            <div class="plugin-card-header">
                <div class="plugin-info">
                    <div class="plugin-name">
                        <span class="plugin-icon">🔍</span>
                        LLM Powered – RAG Query Rewriter
                        <a-tag color="volcano" class="plugin-tag">Advanced RAG</a-tag>
                        <a-tag color="gold" class="plugin-tag">Token Optimizasyonu</a-tag>
                    </div>
                    <div class="plugin-desc">
                        Müşteri mesajını doğrudan embed etmek yerine, bir LLM aracılığıyla vektör aramasına özel, anlamlı ve bağımsız bir sorguya dönüştürür. Knowledge base'inizin yazılı olduğu dile göre otomatik sorgu uyarlaması yapar; "teşekkürler" gibi anlamsız girişlerde RAG aramasını atlayarak token tasarrufu sağlar.
                    </div>
                </div>
                <a-switch :checked="plugins.ragQueryRewrite.enabled" @change="(v) => togglePlugin('ragQueryRewrite', v)" />
            </div>

            <div v-if="plugins.ragQueryRewrite.enabled" class="plugin-detail">
                <div class="detail-section">
                    <div class="detail-label">Knowledge Base Dili <span class="required">*</span></div>
                    <div class="detail-desc">
                        Dokümanlarınızın yüklendiği dili seçin. Müşteri farklı bir dilde yazsa bile sorgu bu dile çevrilerek vektör araması yapılır. Seçilmezse kullanıcının kendi dili korunur.
                    </div>
                    <a-select
                        v-model="plugins.ragQueryRewrite.targetLanguage"
                        style="width:100%"
                        allow-clear
                        placeholder="Dil seçin (opsiyonel)"
                        @change="savePluginField('ragQueryRewrite', 'targetLanguage', $event)"
                    >
                        <a-select-option v-for="lang in ragRewriteLanguages" :key="lang.value" :value="lang.value">
                            {{ lang.label }}
                        </a-select-option>
                    </a-select>
                </div>

                <a-alert type="info" show-icon style="margin-top:12px">
                    <template slot="message">RAG Query Rewriter Nasıl Çalışır?</template>
                    <template slot="description">
                        <p>Bu plugin, her müşteri mesajından önce hafif bir LLM çağrısı yaparak vektör araması için optimize edilmiş bir sorgu üretir:</p>
                        <ol>
                            <li><b>Bağlam Çözümleme</b> – "bu", "onu", "orası" gibi belirsiz referanslar önceki konuşmadan çözümlenir ve sorgu bağımsız hale getirilir.</li>
                            <li><b>Dil Uyarlama</b> – Knowledge base Türkçe ise müşteri İngilizce yazsa bile sorgu Türkçeye çevrilerek arama yapılır; bu sayede semantic benzerlik skoru yükselir.</li>
                            <li><b>Akıllı Fallback</b> – "tamam", "teşekkürler", "merhaba" gibi RAG araması gerektirmeyen mesajlar tespit edilir ve RAG aşaması tamamen atlanır; gereksiz vektör araması ve token maliyeti önlenir.</li>
                            <li><b>Saf Niyet Korunması</b> – LLM soruyu cevaplamaz, yalnızca arama için en uygun ifadeye dönüştürür.</li>
                        </ol>
                        <div style="margin-top:8px">
                            <b>Faydaları:</b>
                            <ul>
                                <li>✅ Çok dilli müşterilerde bile tek dilde tutarlı vektör araması</li>
                                <li>✅ Belirsiz ve çok parçalı sorularda daha yüksek recall</li>
                                <li>✅ Anlamsız mesajlarda RAG atlanarak embedding maliyeti sıfırlanır</li>
                                <li>✅ Ana LLM context penceresi gereksiz belgelerle şişirilmez</li>
                            </ul>
                        </div>
                        <a-alert type="warning" style="margin-top:8px">
                            <template slot="message">Bu plugin yalnızca <b>Supsis RAG</b> modunda çalışır. Her müşteri mesajı için küçük bir ek LLM maliyeti oluşur ve <b>rag-query-rewrite</b> adıyla cüzdanınızdan düşülür.</template>
                        </a-alert>
                    </template>
                </a-alert>
            </div>
        </div>

        <!-- Auto Router -->
        <div class="plugin-card">
            <div class="plugin-card-header">
                <div class="plugin-info">
                    <div class="plugin-name">
                        <span class="plugin-icon">🧭</span>
                        Auto Router
                        <a-tag color="cyan" class="plugin-tag">Otomatik Model Seçimi</a-tag>
                        <a-tag color="green" class="plugin-tag">Ücretsiz</a-tag>
                    </div>
                    <div class="plugin-desc">
                        Her prompt için en uygun modeli otomatik seçer. NotDiamond tarafından desteklenmektedir. Aktif olduğunda seçilen Primary Model yerini <code>openrouter/auto</code>'ya bırakır.
                    </div>
                </div>
                <a-switch :checked="plugins.autoRouter.enabled" @change="(v) => togglePlugin('autoRouter', v)" />
            </div>

            <div v-if="plugins.autoRouter.enabled" class="plugin-detail">
                <div class="detail-section">
                    <div class="detail-label">İzin Verilen Modeller (Opsiyonel)</div>
                    <div class="detail-desc">
                        Boş bırakırsanız Auto Router yüksek kaliteli büyük modeller arasından seçim yapar. Model kısıtlamak için pattern ekleyebilirsiniz.
                    </div>
                    <a-select
                        mode="tags"
                        v-model="plugins.autoRouter.allowedModels"
                        placeholder="Örn: anthropic/*, openai/gpt-5*, google/*"
                        style="width:100%"
                        @change="savePluginField('autoRouter', 'allowedModels', $event)"
                    >
                        <a-select-option v-for="p in autoRouterPresets" :key="p.value" :value="p.value">
                            {{ p.label }}
                        </a-select-option>
                    </a-select>
                    <div class="pattern-hint">
                        <div class="pattern-table">
                            <div class="pattern-row header">
                                <span>Pattern</span><span>Eşleşen Modeller</span>
                            </div>
                            <div class="pattern-row" v-for="p in patternExamples" :key="p.pattern">
                                <code>{{ p.pattern }}</code><span>{{ p.desc }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <a-alert type="info" show-icon style="margin-top:12px">
                    <template slot="message">Auto Router Nasıl Çalışır?</template>
                    <template slot="description">
                        <p>NotDiamond'ın yönlendirme sistemi prompt'unuzu analiz ederek en uygun modeli seçer:</p>
                        <ol>
                            <li><b>Prompt Analizi</b> – Karmaşıklık, görev türü ve model kabiliyetleri değerlendirilir.</li>
                            <li><b>Model Seçimi</b> – Yüksek kaliteli modeller havuzundan optimal model belirlenir.</li>
                            <li><b>Yanıtta Hangi Model Seçildi?</b> – API yanıtında <code>model</code> alanı gerçekte kullanılan modeli gösterir.</li>
                        </ol>
                        <div style="margin-top:8px">
                            <b>Varsayılan Model Havuzu (pattern belirtilmezse):</b>
                            <ul>
                                <li>Claude Sonnet / Opus (Anthropic)</li>
                                <li>GPT-5.x (OpenAI)</li>
                                <li>Gemini 3 Pro (Google)</li>
                                <li>DeepSeek v3 (DeepSeek)</li>
                                <li>ve diğer üst düzey modeller</li>
                            </ul>
                        </div>
                        <a-alert type="success" style="margin-top:8px">
                            <template slot="message">Auto Router'ın kullanımı <b>ücretsizdir</b>. Yalnızca seçilen modelin standart fiyatı ödenir.</template>
                        </a-alert>
                    </template>
                </a-alert>
            </div>
        </div>

        <!-- RAG ReRank -->
        <div class="plugin-card">
            <div class="plugin-card-header">
                <div class="plugin-info">
                    <div class="plugin-name">
                        <span class="plugin-icon">🏆</span>
                        RAG ReRank
                        <a-tag color="purple" class="plugin-tag">Advanced RAG</a-tag>
                        <a-tag color="red" class="plugin-tag">Ücretli</a-tag>
                    </div>
                    <div class="plugin-desc">
                        Vektör aramasından dönen belgeler doğrudan AI'ya verilmek yerine, Voyage AI'ın Reranker modeli ile sorguya göre yeniden sıralanır. En alakalı belgeler üst sıralara taşınarak AI yanıtlarının doğruluğu artırılır.
                    </div>
                </div>
                <a-switch :checked="plugins.ragRerank.enabled" @change="(v) => togglePlugin('ragRerank', v)" />
            </div>

            <div v-if="plugins.ragRerank.enabled" class="plugin-detail">
                <div class="detail-section">
                    <div class="detail-label">ReRank Modeli <span class="required">*</span></div>
                    <div class="detail-desc">Hangi modelin kullanılacağını seçin. Fiyatlar 1.000 token başınadır.</div>
                    <div class="engine-cards">
                        <div
                            v-for="m in rerankModels"
                            :key="m.id"
                            class="engine-card"
                            :class="{ selected: plugins.ragRerank.model === m.id }"
                            @click="savePluginField('ragRerank', 'model', m.id)"
                        >
                            <div class="engine-card-header">
                                <span class="engine-name">{{ m.id }}</span>
                                <a-tag v-if="m.recommended" color="green" style="font-size:11px">Önerilen</a-tag>
                                <a-tag color="orange" style="font-size:11px">{{ m.priceLabel }}</a-tag>
                            </div>
                            <div class="engine-desc">{{ m.description }}</div>
                        </div>
                    </div>
                </div>

                <a-alert type="info" show-icon style="margin-top:12px">
                    <template slot="message">RAG ReRank Nasıl Çalışır?</template>
                    <template slot="description">
                        <p>RAG pipeline'ına ikinci bir aşama ekler; vektör aramasının sıralama hatalarını düzeltir:</p>
                        <ol>
                            <li><b>Vektör Arama</b> – Cosine similarity ile en yakın belgeler bulunur (hız için tasarlandı).</li>
                            <li><b>ReRank</b> – Voyage AI'ın Reranker modeli her belgeyi sorguyla derin semantik benzerlik açısından değerlendirir ve gerçek alaka puanı hesaplar.</li>
                            <li><b>Sıralı Context</b> – En alakalı belgeler üst sıralara alınarak AI'ya verilir; düşük alakalı belgeler sona itilir.</li>
                        </ol>
                        <div style="margin-top:8px">
                            <b>Fiyatlandırma (1.000 token başına):</b>
                            <ul>
                                <li><code>rerank-2.5</code> – $0,000075 / 1K token</li>
                                <li><code>rerank-2.5-lite</code> – $0,00003 / 1K token</li>
                            </ul>
                            <small>* Fiyat: (sorgu token sayısı × belge sayısı) + tüm belge tokenları toplamı üzerinden hesaplanır.</small>
                        </div>
                        <a-alert type="warning" style="margin-top:8px">
                            <template slot="message">Bu plugin yalnızca <b>Supsis RAG</b> modunda çalışır ve her mesaj için <b>ai-rag-rerank</b> adıyla cüzdanınızdan düşülür.</template>
                        </a-alert>
                    </template>
                </a-alert>
            </div>
        </div>

        <div class="save-info" v-if="saved">
            <a-alert type="success" message="Plugin ayarları kaydedildi." closable :after-close="() => saved = false" />
        </div>
    </div>
</template>

<script>
import Vue from "vue";

const PDF_ENGINES = [
    {
        id: "pdf-text",
        name: "pdf-text",
        recommended: true,
        free: true,
        priceLabel: "Ücretsiz",
        description: "Düzgün yapılandırılmış, net metin içeren PDF'ler için idealdir. Faturasız çalışır.",
    },
    {
        id: "mistral-ocr",
        name: "mistral-ocr",
        recommended: false,
        free: false,
        priceLabel: "~$2.20 / 1K sayfa",
        description: "Taranmış belgeler veya görsel içeren PDF'ler için en iyi seçim. Mistral OCR motoru kullanılır.",
    },
    {
        id: "native",
        name: "native",
        recommended: false,
        free: false,
        priceLabel: "Input token ücreti",
        description: "Yalnızca dosya girişini doğal olarak destekleyen modellerde kullanılabilir.",
    },
];

const CONTEXT_SIZES = [
    { id: "low", name: "Düşük", description: "Minimal arama bağlamı. Basit sorgular için uygundur." },
    { id: "medium", name: "Orta", description: "Dengeli bağlam. Genel sorgular için önerilen." },
    { id: "high", name: "Yüksek", description: "Kapsamlı bağlam. Detaylı araştırma gerektiren sorgular için idealdir." },
];

const AUTO_ROUTER_PRESETS = [
    { value: "anthropic/*", label: "anthropic/* — Tüm Anthropic modelleri" },
    { value: "openai/gpt-5*", label: "openai/gpt-5* — Tüm GPT-5 varyantları" },
    { value: "google/*", label: "google/* — Tüm Google modelleri" },
    { value: "*/claude-*", label: "*/claude-* — Tüm Claude modelleri (her sağlayıcı)" },
    { value: "deepseek/*", label: "deepseek/* — Tüm DeepSeek modelleri" },
];

const RERANK_MODELS = [
    {
        id: "rerank-2.5-lite",
        recommended: true,
        priceLabel: "$0,00003 / 1K token",
        description: "Düşük maliyet, yüksek hız. Çoğu kullanım senaryosu için önerilen model.",
    },
    {
        id: "rerank-2.5",
        recommended: false,
        priceLabel: "$0,000075 / 1K token",
        description: "Maksimum doğruluk. Yüksek hassasiyet gerektiren karmaşık doküman setleri için idealdir.",
    },
];

const PATTERN_EXAMPLES = [
    { pattern: "anthropic/*", desc: "Tüm Anthropic modelleri" },
    { pattern: "openai/gpt-5*", desc: "Tüm GPT-5 varyantları" },
    { pattern: "google/*", desc: "Tüm Google modelleri" },
    { pattern: "openai/gpt-5.1", desc: "Yalnızca tam eşleşme" },
    { pattern: "*/claude-*", desc: "Her sağlayıcıdan Claude modelleri" },
];

const RAG_REWRITE_LANGUAGES = [
    { value: "Türkçe", label: "🇹🇷 Türkçe" },
    { value: "English", label: "🇬🇧 English" },
    { value: "Deutsch", label: "🇩🇪 Deutsch" },
    { value: "Français", label: "🇫🇷 Français" },
    { value: "Español", label: "🇪🇸 Español" },
    { value: "Italiano", label: "🇮🇹 Italiano" },
    { value: "Português", label: "🇵🇹 Português" },
    { value: "Русский", label: "🇷🇺 Русский" },
    { value: "العربية", label: "🇸🇦 العربية" },
    { value: "中文", label: "🇨🇳 中文" },
    { value: "日本語", label: "🇯🇵 日本語" },
    { value: "한국어", label: "🇰🇷 한국어" },
    { value: "Nederlands", label: "🇳🇱 Nederlands" },
    { value: "Polski", label: "🇵🇱 Polski" },
    { value: "Čeština", label: "🇨🇿 Čeština" },
    { value: "Română", label: "🇷🇴 Română" },
    { value: "Magyar", label: "🇭🇺 Magyar" },
    { value: "Ελληνικά", label: "🇬🇷 Ελληνικά" },
    { value: "Bahasa Indonesia", label: "🇮🇩 Bahasa Indonesia" },
    { value: "ภาษาไทย", label: "🇹🇭 ภาษาไทย" },
    { value: "Tiếng Việt", label: "🇻🇳 Tiếng Việt" },
    { value: "हिन्दी", label: "🇮🇳 हिन्दी" },
];

const DEFAULT_PLUGINS = function() {
    return {
        responseHealing: { enabled: false },
        pdfInput: { enabled: false, engine: "pdf-text" },
        webSearch: { enabled: false, engine: "", maxResults: 5, searchContextSize: "medium" },
        messageTransform: { enabled: false },
        ragQueryRewrite: { enabled: false, targetLanguage: "" },
        autoRouter: { enabled: false, allowedModels: [] },
        ragRerank: { enabled: false, model: "rerank-2.5-lite" },
    };
};

export default Vue.extend({
    name: "AIPlugins",
    props: {
        storyId: { type: String, required: true },
    },
    data() {
        return {
            plugins: DEFAULT_PLUGINS(),
            pdfEngines: PDF_ENGINES,
            contextSizes: CONTEXT_SIZES,
            autoRouterPresets: AUTO_ROUTER_PRESETS,
            patternExamples: PATTERN_EXAMPLES,
            ragRewriteLanguages: RAG_REWRITE_LANGUAGES,
            rerankModels: RERANK_MODELS,
            saved: false,
        };
    },
    methods: {
        loadSettings() {
            const story = this.$store.state.CB.stories.find(function(s) { return s._id === this.storyId; }, this);
            if (story && story.ai && story.ai.plugins) {
                const p = story.ai.plugins;
                this.plugins = {
                    responseHealing: {
                        enabled: !!(p.responseHealing && p.responseHealing.enabled),
                    },
                    pdfInput: {
                        enabled: !!(p.pdfInput && p.pdfInput.enabled),
                        engine: (p.pdfInput && p.pdfInput.engine) || "pdf-text",
                    },
                    webSearch: {
                        enabled: !!(p.webSearch && p.webSearch.enabled),
                        engine: (p.webSearch && p.webSearch.engine) || "",
                        maxResults: (p.webSearch && p.webSearch.maxResults) || 5,
                        searchContextSize: (p.webSearch && p.webSearch.searchContextSize) || "medium",
                    },
                    messageTransform: {
                        enabled: !!(p.messageTransform && p.messageTransform.enabled),
                    },
                    ragQueryRewrite: {
                        enabled: !!(p.ragQueryRewrite && p.ragQueryRewrite.enabled),
                        targetLanguage: (p.ragQueryRewrite && p.ragQueryRewrite.targetLanguage) || "",
                    },
                    autoRouter: {
                        enabled: !!(p.autoRouter && p.autoRouter.enabled),
                        allowedModels: (p.autoRouter && Array.isArray(p.autoRouter.allowedModels)) ? p.autoRouter.allowedModels : [],
                    },
                    ragRerank: {
                        enabled: !!(p.ragRerank && p.ragRerank.enabled),
                        model: (p.ragRerank && p.ragRerank.model) || "rerank-2.5-lite",
                    },
                };
            }
        },
        async togglePlugin(pluginName, value) {
            this.plugins[pluginName].enabled = value;
            await this.$store.dispatch("CB/updateStorySingleKey", {
                id: this.storyId,
                key: "ai.plugins." + pluginName + ".enabled",
                value: value,
            });
            this.saved = true;
        },
        async savePluginField(pluginName, fieldName, value) {
            this.plugins[pluginName][fieldName] = value;
            await this.$store.dispatch("CB/updateStorySingleKey", {
                id: this.storyId,
                key: "ai.plugins." + pluginName + "." + fieldName,
                value: value,
            });
            this.saved = true;
        },
        async setPdfEngine(engineId) {
            await this.savePluginField("pdfInput", "engine", engineId);
        },
        async setContextSize(sizeId) {
            await this.savePluginField("webSearch", "searchContextSize", sizeId);
        },
    },
    computed: {
        currentStoryAi() {
            var storyId = this.storyId;
            var story = this.$store.state.CB.stories.find(function(s) { return s._id === storyId; });
            return story ? story.ai : null;
        },
    },
    mounted() {
        this.loadSettings();
    },
    watch: {
        currentStoryAi: {
            handler() {
                this.loadSettings();
            },
            deep: true,
        },
        storyId() {
            this.loadSettings();
        },
    },
});
</script>

<style scoped lang="scss">
.ai-plugins {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 860px;

    .plugins-header {
        .plugins-title {
            font-size: 20px;
            font-weight: 700;
            color: #22272d;
            margin-bottom: 6px;
        }
        .plugins-subtitle {
            font-size: 13px;
            color: #666;
            line-height: 1.5;
        }
    }

    .plugin-card {
        background: #fff;
        border: 1.5px solid #e5e5e5;
        border-radius: 14px;
        padding: 20px;
        transition: border-color 0.2s;

        &:hover {
            border-color: #d0d0d0;
        }

        .plugin-card-header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;

            .plugin-info {
                flex: 1;

                .plugin-name {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 15px;
                    font-weight: 600;
                    color: #22272d;
                    margin-bottom: 6px;

                    .plugin-icon {
                        font-size: 18px;
                    }

                    .plugin-tag {
                        font-size: 11px;
                    }
                }

                .plugin-desc {
                    font-size: 13px;
                    color: #666;
                    line-height: 1.5;
                }
            }
        }

        .plugin-detail {
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid #f0f0f0;

            ul {
                padding-left: 20px;
                margin: 6px 0;
                li {
                    margin-bottom: 4px;
                    font-size: 13px;
                    color: #444;
                }
            }

            ol {
                padding-left: 20px;
                margin: 6px 0;
                li {
                    margin-bottom: 4px;
                    font-size: 13px;
                    color: #444;
                }
            }

            p {
                font-size: 13px;
                color: #444;
                margin-bottom: 6px;
            }

            .detail-label {
                font-size: 13px;
                font-weight: 600;
                color: #22272d;
                margin-bottom: 4px;

                .required {
                    color: red;
                }
            }

            .detail-desc {
                font-size: 12px;
                color: #888;
                margin-bottom: 10px;
            }

            .detail-row {
                display: flex;
                gap: 16px;
            }

            .detail-section {
                margin-bottom: 12px;
            }

            .engine-cards {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }

            .engine-card {
                border: 2px solid #e5e5e5;
                border-radius: 10px;
                padding: 12px 14px;
                cursor: pointer;
                transition: all 0.2s;
                background: #fff;

                &:hover {
                    border-color: #1890ff;
                }

                &.selected {
                    border-color: #1890ff;
                    background: #e6f7ff;
                }

                .engine-card-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 4px;

                    .engine-name {
                        font-weight: 600;
                        font-size: 13px;
                        font-family: monospace;
                    }
                }

                .engine-desc {
                    font-size: 12px;
                    color: #666;
                }
            }

            .context-cards {
                display: flex;
                gap: 10px;
                margin-top: 8px;

                .context-card {
                    flex: 1;
                    border: 2px solid #e5e5e5;
                    border-radius: 10px;
                    padding: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-align: center;

                    &:hover {
                        border-color: #1890ff;
                    }

                    &.selected {
                        border-color: #1890ff;
                        background: #e6f7ff;
                    }

                    .context-name {
                        font-weight: 600;
                        font-size: 13px;
                        margin-bottom: 4px;
                    }

                    .context-desc {
                        font-size: 11px;
                        color: #888;
                        line-height: 1.4;
                    }
                }
            }

            .pattern-hint {
                margin-top: 12px;

                .pattern-table {
                    border: 1px solid #e5e5e5;
                    border-radius: 8px;
                    overflow: hidden;

                    .pattern-row {
                        display: grid;
                        grid-template-columns: 1fr 2fr;
                        padding: 8px 12px;
                        font-size: 12px;
                        border-bottom: 1px solid #f0f0f0;

                        &:last-child {
                            border-bottom: none;
                        }

                        &.header {
                            background: #fafafa;
                            font-weight: 600;
                            color: #444;
                        }

                        code {
                            font-family: monospace;
                            background: #f5f5f5;
                            padding: 2px 6px;
                            border-radius: 4px;
                            font-size: 11px;
                        }

                        span {
                            color: #666;
                        }
                    }
                }
            }
        }
    }

    .save-info {
        margin-top: 8px;
    }
}
</style>
